import { IOfficeWindow, SignatureData } from "./../../intake_shared/src/types";
import {
    ClinicianOfficeWindowRequests,
    OfficeManagerActions,
    OfficeRequestStatus,
} from "intake_shared/src/types";

import db from "../utils/firestore";
import {
    doc,
    arrayUnion,
    getDocs,
    collection,
    writeBatch,
} from "firebase/firestore";
import { Collection } from "intake_shared/src/enum";
import moment from "moment";
import { getOfficeManagers } from "./cache/location";
import {
    sendOfficeWindowDeleteRequestActionEmails,
    sendOfficeWindowRequestActionEmails,
    sendRequestedOfficeWindowEmails,
} from "../utils/queue_email";

/**
 * Reads only the documents up to and including the requested page, then
 * slices that page out. Firestore bills one read per returned document, so
 * page 1 of a 2,000-document result now costs 50 reads instead of 2,050
 * (the previous full scan plus a second query for the page itself).
 */
async function getPageDocs<T>(
    query: { limit(limit: number): { get(): Promise<{ docs: T[] }> } },
    page: number,
    pageSize: number
): Promise<T[]> {
    const startIndex = (Math.max(page, 1) - 1) * pageSize;
    const snapshot = await query.limit(startIndex + pageSize).get();
    return snapshot.docs.slice(startIndex);
}

// function to determine if a new request matches an existing request in the office window request collection
const doesNewRequestMatchExistingRequest = (
    req: ClinicianOfficeWindowRequests,
    category: string,
    request: ClinicianOfficeWindowRequests
) => {
    return (
        moment((req.start_date as any).toDate()).isSame(
            request.start_date,
            "minute"
        ) &&
        moment((req.end_date as any).toDate()).isSame(request.end_date, "minute") &&
        req.location === request.location &&
        req.office === request.office &&
        req.category === category
    );
};

// function to update request or waitlist count
async function updateRequestCounts(
    existingRequests: ClinicianOfficeWindowRequests[],
    category: string,
    request: ClinicianOfficeWindowRequests
) {
    // check if the new request matches any of the existing request
    const updatedRequests = existingRequests
        .filter((req: ClinicianOfficeWindowRequests) =>
            doesNewRequestMatchExistingRequest(req, category, request)
        )
        .map((req: any) => {
            if (category === "request_list") {
                return { ...req, request_list_count: req.request_list_count + 1 };
            } else if (category === "wait_list") {
                return { ...req, waitlist_count: req.waitlist_count + 1 };
            }
            return req;
        });

    // return id, request_list_count, waitlist_count from the requests that match
    const updatedRequestData = updatedRequests.map(
        (req: ClinicianOfficeWindowRequests) => ({
            office_window_request_id: req.office_window_request_id,
            request_list_count: req.request_list_count || 0,
            waitlist_count: req.waitlist_count || 0,
        })
    );

    const requestDetails = {
        office_window_request_id: request.office_window_request_id,
        request_list_count: updatedRequests[0]?.request_list_count,
        waitlist_count: request.waitlist_count,
    };
    const waitListDetails = {
        office_window_request_id: request.office_window_request_id,
        request_list_count: request?.request_list_count,
        waitlist_count: updatedRequests[0]?.waitlist_count,
    };

    // if there is a request that matches update the count of the new request and add it alongside the returned existing requests
    if (updatedRequestData.length > 0 && category === "request_list") {
        updatedRequestData.push(requestDetails);
    }

    if (updatedRequestData.length > 0 && category === "wait_list") {
        updatedRequestData.push(waitListDetails);
    }

    return updatedRequestData;
}

// create office window request
export async function createClinicianOfficeWindowRequests({
    clinician_request_data,
    intakeSignature,
}: {
    clinician_request_data: ClinicianOfficeWindowRequests;
    intakeSignature: SignatureData;
}) {
    try {
        // get the existing requests
        const existingRequestsSnapshot = await getDocs(
            collection(db, Collection.OFFICE_WINDOW_REQUESTS)
        );
        const existingRequests = existingRequestsSnapshot.docs.map(
            (doc) => doc.data() as ClinicianOfficeWindowRequests
        );

        let updatedCounts;
        // get the updated requests counts
        if (clinician_request_data.category !== "delete_office_window_list") {
            updatedCounts = await updateRequestCounts(
                existingRequests,
                clinician_request_data.category as string,
                clinician_request_data
            );
        }

        // new request to be created
        const newRequest = {
            ...clinician_request_data,
            status:
                clinician_request_data.category === "wait_list"
                    ? OfficeRequestStatus.ON_WAITLIST
                    : OfficeRequestStatus.PENDING,
            request_list_count:
                updatedCounts?.find(
                    (request) =>
                        request.office_window_request_id ===
                        clinician_request_data.office_window_request_id
                )?.request_list_count || clinician_request_data.request_list_count,
            waitlist_count:
                updatedCounts?.find(
                    (request) =>
                        request.office_window_request_id ===
                        clinician_request_data.office_window_request_id
                )?.waitlist_count || clinician_request_data.waitlist_count,
            ending_date: clinician_request_data.ending_date || "",
        };

        // Create a batch to update the entire office_window_requests collection
        const batch = writeBatch(db);

        // Set the new request
        batch.set(
            doc(
                db,
                Collection.OFFICE_WINDOW_REQUESTS,
                clinician_request_data.office_window_request_id as string
            ),
            newRequest
        );

        // Update the counts fields for all requests (existing and new)
        updatedCounts?.forEach((updated: any) => {
            batch.update(
                doc(
                    db,
                    Collection.OFFICE_WINDOW_REQUESTS,
                    updated.office_window_request_id
                ),
                {
                    request_list_count: updated.request_list_count,
                    waitlist_count: updated.waitlist_count,
                }
            );
        });

        // Commit the batch
        await batch.commit();

        clinician_request_data.location !== "Remote" &&
            sendRequestedOfficeWindowEmails(clinician_request_data, intakeSignature);
        return true;
    } catch (err) {
        console.error("Error creating request:", err);
        return false;
    }
}

// delete office window request
export async function deleteOfficeWindowRequests(officeWindowId: string) {
    try {
        const snapshot = await db
            .collection(Collection.OFFICE_WINDOW_REQUESTS)
            .doc(officeWindowId)
            .get();

        if (snapshot.exists) {
            const request = snapshot.data() as ClinicianOfficeWindowRequests;

            const { category, location, office, start_date, end_date } = request;

            const matchingRequestsSnapshot = await db
                .collection(Collection.OFFICE_WINDOW_REQUESTS)
                .where("category", "==", category)
                .where("location", "==", location)
                .where("office", "==", office)
                .where("start_date", "==", start_date)
                .where("end_date", "==", end_date)
                .get();

            const batch = db.batch();

            matchingRequestsSnapshot.forEach((doc) => {
                const requestRef = db
                    .collection(Collection.OFFICE_WINDOW_REQUESTS)
                    .doc(doc.id);
                if (category === "request_list") {
                    batch.update(requestRef, {
                        request_list_count: doc.data().request_list_count - 1,
                    });
                } else if (category === "wait_list") {
                    batch.update(requestRef, {
                        waitlist_count: doc.data().waitlist_count - 1,
                    });
                }
            });

            await batch.commit();

            await snapshot.ref.delete();
            return true;
        } else {
            console.error("Office window request not found.");
            return false;
        }
    } catch (error) {
        console.error("Error deleting office window request:", error);
        return false;
    }
}

// get clinician office window requests by email
export async function findOfficeWindowRequestsByClinicianEmail(
    clinician_email: string,
    page: number = 1,
    pageSize: number = 50,
    filters?: {
        location?: string;
        office?: string;
        status?: string;
        category?: string;
        categories?: string[];
    }
) {
    let query = db
        .collection(Collection.OFFICE_WINDOW_REQUESTS)
        .where("requester", "==", clinician_email);

    if (filters) {
        if (filters.category) {
            query = query.where("category", "==", filters.category);
        } else if (filters.categories && filters.categories.length > 0) {
            query = query.where("category", "in", filters.categories);
        }
        if (filters.location) {
            query = query.where("location", "==", filters.location);
        }
        if (filters.office) {
            query = query.where("office", "==", filters.office);
        }
        if (filters.status) {
            query = query.where("status", "==", filters.status);
        }
    }

    query = query.orderBy("request_date", "desc");

    const pageDocs = await getPageDocs(query, page, pageSize);

    return pageDocs.map((doc) => {
        const data = doc.data() as any;
        return {
            ...data,
            start_date: data.start_date.toDate(),
            end_date: data.end_date.toDate(),
            request_date: data.request_date.toDate(),
        };
    });
}

export async function findOfficeWindowRequestsByIds(ids: string[]) {
    let requests: any[] = [];

    while (ids.length > 0) {
        const id_set = ids.splice(0, 10);
        const raw_data = (
            await db
                .collection(Collection.OFFICE_WINDOW_REQUESTS)
                .where("office_window_request_id", "in", id_set)
                .get()
        ).docs;

        requests = requests.concat(
            raw_data.map((doc) => {
                const data = doc.data() as any;
                return {
                    ...data,
                    start_date: data.start_date.toDate(),
                    end_date: data.end_date.toDate(),
                    request_date: data.request_date.toDate(),
                };
            })
        );
    }

    return requests;
}

// get office request list for office managers by their location

export async function findOfficeWindowRequestsByCategory(
    loggedInUserEmail: string,
    category: string,
    page: number = 1,
    pageSize: number = 50,
    filters?: {
        location?: string;
        office?: string;
        clinician_name?: string;
        status?: string;
        day?: string;
    }
) {
    const getOfficeManager = await getOfficeManagers();
    const officeManagerLocationsofLoggedInUser = Object.keys(
        getOfficeManager || {}
    )?.filter((location) => {
        return (
            getOfficeManager &&
            getOfficeManager[location]?.includes(loggedInUserEmail)
        );
    });

    // Check if officeManagerLocationsofLoggedInUser is empty
    if (officeManagerLocationsofLoggedInUser?.length === 0) {
        throw new Error(
            "Your current role does not include office management responsibilities."
        );
    }

    let query = db
        .collection(Collection.OFFICE_WINDOW_REQUESTS)
        .where("category", "==", category)
        .where("location", "in", officeManagerLocationsofLoggedInUser);

    // Apply filters if provided
    if (filters) {
        if (filters.location) {
            query = query.where("location", "==", filters.location);
        }
        if (filters.day) {
            query = query.where("day", "==", filters.day);
        }
        if (filters.office) {
            query = query.where("office", "==", filters.office);
        }
        if (filters.status) {
            query = query.where("status", "==", filters.status);
        }
    }

    // Sort the collection by request_date
    query = query.orderBy("request_date", "desc");

    // The clinician_name filter runs client-side, so it needs every match.
    // Without it, documents past the requested page are never shown.
    const allDocumentsSnapshot = await (filters?.clinician_name
        ? query
        : query.limit(Math.max(page, 1) * pageSize)
    ).get();

    let requests = allDocumentsSnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
            ...data,
            start_date: data.start_date.toDate(),
            end_date: data.end_date.toDate(),
            request_date: data.request_date.toDate(),
            ending_date: data.ending_date ? data.ending_date : "",
        };
    });

    // Filter by clinician_name if provided
    if (filters && filters.clinician_name) {
        const keyword = filters.clinician_name.toLowerCase();
        requests = requests.filter((request) =>
            request.clinician_name.toLowerCase().includes(keyword)
        );
    }

    // Paginate the filtered results
    const startIndex = (page - 1) * pageSize;
    const paginatedRequests = requests.slice(startIndex, startIndex + pageSize);

    return paginatedRequests;
}

export async function findClinicianOfficeWindowRequestsByCategory(
    loggedInUserEmail: string,
    category: string,
    page: number = 1,
    pageSize: number = 50,
    filters?: {
        location?: string;
        office?: string;
        clinician_name?: string;
        status?: string;
        day?: string;
    }
) {
    let query = db
        .collection(Collection.OFFICE_WINDOW_REQUESTS)
        .where("category", "==", category)
        .where("requester", "==", loggedInUserEmail);

    // Apply filters if provided
    if (filters) {
        if (filters.location) {
            query = query.where("location", "==", filters.location);
        }
        if (filters.day) {
            query = query.where("day", "==", filters.day);
        }
        if (filters.office) {
            query = query.where("office", "==", filters.office);
        }
        if (filters.status) {
            query = query.where("status", "==", filters.status);
        }
    }

    // Sort the collection by request_date
    query = query.orderBy("request_date", "desc");

    // The clinician_name filter runs client-side, so it needs every match.
    // Without it, documents past the requested page are never shown.
    const allDocumentsSnapshot = await (filters?.clinician_name
        ? query
        : query.limit(Math.max(page, 1) * pageSize)
    ).get();

    let requests = allDocumentsSnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
            ...data,
            start_date: data.start_date.toDate(),
            end_date: data.end_date.toDate(),
            request_date: data.request_date.toDate(),
            ending_date: data.ending_date ? data.ending_date : "",
        };
    });

    // Filter by clinician_name if provided
    if (filters && filters.clinician_name) {
        const keyword = filters.clinician_name.toLowerCase();
        requests = requests.filter((request) =>
            request.clinician_name.toLowerCase().includes(keyword)
        );
    }

    // Paginate the filtered results
    const startIndex = (page - 1) * pageSize;
    const paginatedRequests = requests.slice(startIndex, startIndex + pageSize);

    return paginatedRequests;
}

// approve office window request
export async function approveClinicianOfficeWindow({
    clinicianId,
    id,
    office,
    start,
    end,
    ending_date,
    location,
    status,
    assigned_to,
    available,
    office_window_request_id,
    approve_with_changes_reason,
    office_manager,
    category,
    reason,
    intakeSignature,
}: {
    clinicianId: string;
    id: string;
    office: string;
    start: string;
    end: string;
    location: string;
    status: string;
    assigned_to: string;
    available: boolean;
    office_window_request_id: string;
    approve_with_changes_reason?: string;
    office_manager: string;
    category: string;
    ending_date?: string;
    reason?: string;
    intakeSignature: SignatureData;
}) {
    // Update CLINICIANS collection
    if (category !== "delete_office_window_list") {
        await db
            .collection(Collection.CLINICIANS)
            .doc(clinicianId)
            .update({
                office_windows: arrayUnion({
                    id,
                    office,
                    start,
                    end,
                    location,
                    ending_date,
                }),
                locations: arrayUnion(location),
            });
    }

    // Update OFFICE_WINDOW_REQUESTS collection
    const updateFields = {
        assigned_to,
        status,
        available,
        office,
        start_date: new Date(start),
        end_date: new Date(end),
    };

    if (approve_with_changes_reason) {
        updateFields["approve_with_changes_reason"] = approve_with_changes_reason;
    }

    await db
        .collection(Collection.OFFICE_WINDOW_REQUESTS)
        .doc(office_window_request_id)
        .update(updateFields);

    let activityLabel;

    if (category === "delete_office_window_list") {
        activityLabel = OfficeManagerActions.APPROVED_OFFICE_WINDOW_DELETE_REQUEST;
    } else {
        activityLabel = approve_with_changes_reason
            ? OfficeManagerActions.APPROVED_WITH_CHANGES
            : OfficeManagerActions.APPROVED_REQUEST;
    }

    // Log the activity to the office_manager_activities collection
    const activityLog = {
        timestamp: new Date(),
        activity: activityLabel,
        office_manager: office_manager,
        office_window_request_id,
        office,
        start,
        end,
        requested_by: clinicianId,
        location,
        ...(approve_with_changes_reason && {
            approved_with_changes: approve_with_changes_reason,
        }),
        ...(assigned_to && { assigned_to }),
    };

    await db
        .collection(Collection.OFFICE_MANAGER_ACTIVITIES)
        .doc(office_window_request_id)
        .set(activityLog);

    const emailData = {
        start_date: new Date(start),
        end_date: new Date(end),
        location: location,
        office: office,
        reason_for_delete: reason,
        category: category,
    };

    if (category === "delete_office_window_list") {
        sendOfficeWindowDeleteRequestActionEmails(
            clinicianId,
            emailData,
            "Approved",
            intakeSignature
        );
    } else {
        sendOfficeWindowRequestActionEmails(
            clinicianId,
            emailData,
            approve_with_changes_reason ? "Approved with changes" : "Approved",
            intakeSignature
        );
    }
}

// reject office window request
export async function rejectClinicianOfficeWindow({
    office_request_to_reject_details,
    type,
    intakeSignature,
}: {
    office_request_to_reject_details: {
        start_date: Date;
        end_date: Date;
        location: string;
        office: string;
        rejection_reason: string;
        office_window_request_id: string;
        status: string;
        office_manager: string;
        requester: string;
        clinician_id?: string;
        category?: string;
    };
    type?: string;
    intakeSignature: SignatureData;
}) {
    // Update OFFICE_WINDOW_REQUESTS collection
    const updateFields = {
        rejection_reason: office_request_to_reject_details.rejection_reason,
        status: office_request_to_reject_details.status,
    };
    await db
        .collection(Collection.OFFICE_WINDOW_REQUESTS)
        .doc(office_request_to_reject_details.office_window_request_id)
        .update(updateFields);

    // Log the activity to the office_manager_activities collection
    const activityLog = {
        timestamp: new Date(),
        activity:
            type === "delete_office_window_request"
                ? OfficeManagerActions.REJECTED_OFFICE_WINDOW_DELETE_REQUEST
                : OfficeManagerActions.REJECTED_REQUEST,
        office_manager: office_request_to_reject_details.office_manager,
        office: office_request_to_reject_details.office,
        requested_by: office_request_to_reject_details.requester,
        start: office_request_to_reject_details.start_date.toString(),
        end: office_request_to_reject_details.end_date.toString(),
        location: office_request_to_reject_details.location,
        reason: office_request_to_reject_details.rejection_reason,
    };
    const emailData = {
        start_date: office_request_to_reject_details.start_date,
        end_date: office_request_to_reject_details.end_date,
        location: office_request_to_reject_details.location,
        office: office_request_to_reject_details.office,
        reason: office_request_to_reject_details.rejection_reason,
        category: office_request_to_reject_details.category,
    };

    if (type === "delete_office_window_request") {
        sendOfficeWindowDeleteRequestActionEmails(
            office_request_to_reject_details.requester,
            emailData,
            "Rejected",
            intakeSignature
        );
    } else {
        sendOfficeWindowRequestActionEmails(
            office_request_to_reject_details.requester,
            emailData,
            "Rejected",
            intakeSignature
        );
    }

    await db
        .collection(Collection.OFFICE_MANAGER_ACTIVITIES)
        .doc(office_request_to_reject_details.office_window_request_id)
        .set(activityLog);
}

// function to retrieve items from the "office_manager_activities" collection
export async function retrieveOfficeManagerActivities(
    loggedInUserEmail: string,
    page: number = 1,
    pageSize: number = 50,
    filters?: {
        location?: string;
        office?: string;
        activity?: string;
    }
) {
    try {
        let query = db
            .collection(Collection.OFFICE_MANAGER_ACTIVITIES)
            .where("office_manager", "==", loggedInUserEmail);

        // Apply filters if provided
        if (filters) {
            if (filters.location) {
                query = query.where("location", "==", filters.location);
            }

            if (filters.office) {
                query = query.where("office", "==", filters.office);
            }
            if (filters.activity) {
                query = query.where("activity", "==", filters.activity);
            }
        }

        // Sort the collection by request_date
        query = query.orderBy("timestamp", "desc");

        const pageDocs = await getPageDocs(query, page, pageSize);
        const requests = pageDocs.map((doc) => {
            const data = doc.data() as any;
            const timestamp = data.timestamp.toDate();
            return {
                ...data,
                start: new Date(data.start),
                end: new Date(data.end),
                timestamp: timestamp.toISOString(),
            };
        });

        return requests;
    } catch (error) {
        console.error("Error retrieving office manager activities:", error);
        return [];
    }
}

export async function createAvailableOfficeWindows({
    office_window_data,
    loggedInUserEmail,
    type,
}: {
    office_window_data: IOfficeWindow;
    loggedInUserEmail: string;
    type: "automatic_creation" | "created_by_office_manager";
}) {
    try {
        const officeWindowRef = db
            .collection(Collection.OFFICE_WINDOWS)
            .doc(office_window_data.id);

        await officeWindowRef.set(office_window_data);

        // Log the activity to the office_manager_activities collection
        const activityLog = {
            timestamp: new Date(),
            activity: OfficeManagerActions.CREATED_OFFICE_WINDOW,
            office_manager: loggedInUserEmail,
            office_window_id: office_window_data.id,
            office: office_window_data.office,
            start: office_window_data.start,
            end: office_window_data.end,
            location: office_window_data.location,
        };

        await db
            .collection(Collection.OFFICE_MANAGER_ACTIVITIES)
            .doc(office_window_data.id)
            .set(activityLog);

        return true;
    } catch (error) {
        console.error("Error setting office window:", error);
        throw error;
    }
}

export async function getOfficeWindows(
    loggedInUserEmail: string,
    viewFrom: "create_available_office_window" | "get_available_office_windows",
    page: number = 1,
    pageSize: number = 50,
    filters?: {
        location?: string;
        office?: string;
    }
) {
    const getOfficeManager = await getOfficeManagers();
    const officeManagerLocationsofLoggedInUser = Object.keys(
        getOfficeManager || {}
    )?.filter((location) => {
        return (
            getOfficeManager &&
            getOfficeManager[location]?.includes(loggedInUserEmail)
        );
    });

    // Check if officeManagerLocationsofLoggedInUser is empty
    if (officeManagerLocationsofLoggedInUser.length === 0) {
        throw new Error(
            "Your current role does not include office management responsibilities."
        );
    }

    let query =
        viewFrom === "create_available_office_window"
            ? db.collection(Collection.OFFICE_WINDOWS)
            : db
                  .collection(Collection.OFFICE_WINDOWS)
                  .where("location", "in", officeManagerLocationsofLoggedInUser);

    // Apply filters if provided
    if (filters) {
        if (filters.location) {
            query = query.where("location", "==", filters.location);
        }

        if (filters.office) {
            query = query.where("office", "==", filters.office);
        }
    }

    // Sort the collection by request_date
    query = query.orderBy("start", "desc");

    const pageDocs = await getPageDocs(query, page, pageSize);

    const requests = pageDocs.map((doc) => {
        const data = doc.data() as any;
        return data;
    });

    return requests;
}

export async function getPotentialCoveringOfficeWindows(
    loggedInUserEmail: string,
    officeWindowToDelete: IOfficeWindow
) {
    const getOfficeManager = await getOfficeManagers();
    const officeManagerLocationsOfLoggedInUser = Object.keys(
        getOfficeManager || {}
    ).filter((officeManagerLocation) => {
        return (
            getOfficeManager &&
            getOfficeManager[officeManagerLocation]?.includes(loggedInUserEmail)
        );
    });

    if (
        !officeManagerLocationsOfLoggedInUser.includes(officeWindowToDelete.location)
    ) {
        throw new Error(
            "Your current role does not include office management responsibilities for this location."
        );
    }

    const querySnapshot = await db
        .collection(Collection.OFFICE_WINDOWS)
        .where("location", "==", officeWindowToDelete.location)
        .where("office", "==", officeWindowToDelete.office)
        .get();

    return querySnapshot.docs.map((doc) => doc.data() as IOfficeWindow);
}

export async function deleteOfficeWindowById(
    officeWindowToDelete: IOfficeWindow,
    loggedInUserEmail: string,
    reason: string
) {
    try {
        const officeWindowRef = db
            .collection(Collection.OFFICE_WINDOWS)
            .doc(officeWindowToDelete.id);

        const snapshot = await officeWindowRef.get();

        if (snapshot.exists) {
            await officeWindowRef.delete();

            // Log the activity to the office_manager_activities collection
            const activityLog = {
                timestamp: new Date(),
                activity: OfficeManagerActions.DELETED_OFFICE_WINDOW,
                office_manager: loggedInUserEmail,
                office_window_id: officeWindowToDelete.id,
                office: officeWindowToDelete.office,
                start: officeWindowToDelete.start,
                end: officeWindowToDelete.end,
                location: officeWindowToDelete.location,
                reason: reason,
            };

            await db
                .collection(Collection.OFFICE_MANAGER_ACTIVITIES)
                .doc(officeWindowToDelete.id)
                .set(activityLog);

            return true;
        } else {
            console.error("Office window not found.");
            return false;
        }
    } catch (error) {
        console.error("Error deleting office window:", error);
        throw error;
    }
}

export async function blockOrUnblockOfficeWindowById(
    officeWindowId: string,
    action: boolean,
    officeDetailsToBlock?: {
        blocked_reason: string;
        start_date: string;
        end_date: string;
    }
) {
    try {
        const officeWindowRef = db
            .collection(Collection.OFFICE_WINDOWS)
            .doc(officeWindowId);

        const snapshot = await officeWindowRef.get();
        // if action is true we are blocking but we are unblocking when it is false
        const details = action
            ? {
                  blocked: action,
                  ...officeDetailsToBlock,
              }
            : {
                  blocked: action,
                  start_date: "",
                  end_date: "",
                  blocked_reason: "",
              };
        if (snapshot.exists) {
            await officeWindowRef.update({
                ...details,
            });
            return true;
        } else {
            console.error("Office window not found.");
            return false;
        }
    } catch (error) {
        console.error("Error blocking office window:", error);
        throw error;
    }
}

export async function handleHoldOfficeSpace(
    office_request_status: OfficeRequestStatus,
    officeWindowRequestId: string,
    action: "hold_with_changes" | "hold_window" | undefined,
    reason_for_holding: string,
    startDate: Date,
    endDate: Date,
    office: string,
    office_manager: string,
    location: string,
    requester: string
) {
    try {
        // Update OFFICE_WINDOW_REQUESTS collection
        const updateFields = {
            status: office_request_status,
            reason_for_holding: reason_for_holding,
        };

        if (action === "hold_with_changes") {
            updateFields["start_date"] = startDate;
            updateFields["end_date"] = endDate;
            updateFields["office"] = office;
        }
        await db
            .collection(Collection.OFFICE_WINDOW_REQUESTS)
            .doc(officeWindowRequestId)
            .update(updateFields);

        // Log the activity to the office_manager_activities collection
        const activityLog = {
            timestamp: new Date(),
            activity:
                action === "hold_with_changes"
                    ? OfficeManagerActions.HELD_WINDOW_WITH_CHANGES
                    : OfficeManagerActions.HELD_WINDOW,
            office_manager: office_manager,
            office: office,
            start: startDate.toString(),
            end: endDate.toString(),
            location: location,
            requested_by: requester,
        };
        await db
            .collection(Collection.OFFICE_MANAGER_ACTIVITIES)
            .doc(officeWindowRequestId)
            .set(activityLog);

        return true;
    } catch (error) {
        console.error("Error holding office window:", error);
        throw error;
    }
}
export async function getAllHeldOfficeWindowsByLocation(location: string) {
    let query = db
        .collection(Collection.OFFICE_WINDOW_REQUESTS)
        .where("status", "==", OfficeRequestStatus.HELD)
        .where("location", "==", location);

    const querySnapshot = await query.get();
    const requests = querySnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
            ...data,
            start_date: data.start_date.toDate(),
            end_date: data.end_date.toDate(),
            request_date: data.request_date.toDate(),
        };
    });
    return requests;
}

export async function getUnPaginatedOfficeWndows(location: string) {
    let query = db
        .collection(Collection.OFFICE_WINDOWS)
        .where("location", "==", location);

    const querySnapshot = await query.get();
    const requests = querySnapshot.docs.map((doc) => {
        const data = doc.data() as any;
        return {
            ...data,
        };
    });
    return requests;
}
